import React from "react";
import { useCvpQueryClient } from "../../../api/queryClient";
import styled from "styled-components";
import { candidateAPI } from "../../../api/candidate";
import { CheckWasDeleteRequestedResponse } from "../../../api/candidate/checkWasDeleteRequested";
import { CvpButton } from "../../../generic-components/buttons/CvpButton";
import { getFormSubmitErrorMessage } from "../../forms/formUtils/handleFormSubmitError";
import { showErrorToastMsg } from "../../../generic-components/messages/CvpToastMessages";
import { device } from "../../../constants/mediaQueries";
import { useConsentsForCandidate } from "../../../user/useConsentsForCandidate";
import { wsConfirmDialog } from "../../common/wsConfirmDialog";
import { useLocStore } from "../../../localization";
import { LocContext } from "../../../localization/LocContext";

import dayjs from "dayjs";
import { VISUAL_DATE_FORMAT } from "../../../constants/globalConsts";
import { useLogOut } from "../../../url/logOut";

interface DeleteMeContentProps {
  wasDeleteRequested: CheckWasDeleteRequestedResponse;
  company: string;
  contentText: React.ReactNode;
  cancelButton?: React.ReactNode;
  closeButton?: React.ReactNode;
}

export const DeleteMeContent = (props: DeleteMeContentProps) => {
  const {
    wasDeleteRequested,
    contentText,
    cancelButton,
    company,
    closeButton,
  } = props;
  const L = useLocStore(LocContext.GENERAL);
  const cvpQueryClient = useCvpQueryClient();
  const logOut = useLogOut();

  const { consent5000 } = useConsentsForCandidate();

  const handleDeleteUser = async () => {
    try {
      const response = await wsConfirmDialog({
        header: L.id("bdf4d5345ec5").loc("Slette profil?"),
        message: L.id("37e47f170436").loc(
          "ADVARSEL: Når slettingen er fullført er det ikke mulig å angre. Alle dine lagrede dokumenter, personopplysninger og andre data forsvinner for godt. Trykk 'ja' dersom du er helt sikker på at du vil at brukeren din slettes."
        ),
        icon: "pi pi-warning",
      });

      if (response) {
        if (consent5000.consent === false) {
          try {
            await candidateAPI.endConsentForCandidate({ consentType: 5000 });
          } catch (error) {
            const withCorrelationId = getFormSubmitErrorMessage(error);
            showErrorToastMsg(withCorrelationId);
          }
        }

        try {
          await candidateAPI.deleteMe({});
        } catch (error) {
          const withCorrelationId = getFormSubmitErrorMessage(error);
          showErrorToastMsg(withCorrelationId);
        }

        // Introduce a delay before invalidating queries
        setTimeout(() => {
          cvpQueryClient.invalidateQueries();
        }, 2000); // Adjust the delay time as needed
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formatDeleteDate = dayjs(wasDeleteRequested?.item).format(
    VISUAL_DATE_FORMAT
  );

  return (
    <StyledDeleteMeContent>
      <h2 className="cvp-h2">{L.id("d9471daa046b").loc("Slett min profil")}</h2>
      {!wasDeleteRequested?.item ? (
        <>
          <div className="text-container">{contentText}</div>

          <div className="btn-container">
            {cancelButton}

            <CvpButton
              label={L.id("9f760d189175").loc(
                "Jeg ønsker å slette brukeren min"
              )}
              onClick={handleDeleteUser}
              type="button"
              icon="pi pi-trash"
              severity="danger"
            />
          </div>
        </>
      ) : (
        <StyledIsDeletedContent>
          <p className="is-deleted-text">
            {L.id("fd798b7474e5").split(
              "Du har bedt om å bli slettet den {0}. Det tar litt tid før forespørselen blir behandlet. Det er {1} som utfører slettingen. Når du ikke lenger kan logge inn har dataene dine blitt slettet. Ta kontakt med {1} hvis du har spørsmål.",
              [formatDeleteDate ?? "", company]
            )}
          </p>
          <div className="log-out-btn-container">
            {closeButton}
            <CvpButton
              label={L.id("086b07553c42").loc("Logg ut")}
              onClick={logOut}
              type="button"
              icon="pi pi-sign-out"
              text
            />
          </div>
        </StyledIsDeletedContent>
      )}
    </StyledDeleteMeContent>
  );
};

const StyledIsDeletedContent = styled.a`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .log-out-btn-container {
    display: flex;
    justify-content: flex-end;
    padding-right: 1rem;

    .p-button-label {
      font-size: 0.9em;
    }
  }

  .is-deleted-text {
    font-size: 0.9em;
    color: var(--red-700);
    line-height: 1.5;
  }
`;

const StyledDeleteMeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  h2 {
    color: var(--red-500);
  }

  .text-container {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    padding: 1rem 0;

    p {
      line-height: 1.5;
      padding: 0;
      margin: 0;
    }
  }

  .btn-container {
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-end;
    gap: 1rem;

    @media ${device.md} {
      flex-direction: row;
    }

    .p-button {
      width: fit-content;
    }
  }
`;
